import { Presentation, FileQuestion, NotebookPen, Star, Sparkles } from "lucide-react";

const colorMap = {
  sky: "bg-sky-100 text-sky-600",
  emerald: "bg-emerald-100 text-emerald-600",
  amber: "bg-amber-100 text-amber-600",
  indigo: "bg-indigo-100 text-indigo-600",
  rose: "bg-rose-100 text-rose-600",
};

export default function ResourceContent({ resource, onEditImage }) {
  if (!resource) return null;
  const c = resource.content || {};

  if (resource.type === "presentation") {
    const tc = c.theme_color || "#0CA5E9";
    return (
      <div className="space-y-4" data-testid="content-presentation">
        {(c.slides || []).map((s, i) => (
          <div key={i} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: tc }}>{i + 1}</span>
              <h3 className="font-heading font-bold text-lg" style={{ color: tc }}>{s.title}</h3>
            </div>
            <div className="flex gap-4">
              <ul className="space-y-1.5 pl-2 flex-1">
                {(s.bullets || []).map((b, j) => (
                  <li key={j} className="flex gap-2 text-slate-600 text-sm">
                    <Sparkles size={14} className="text-amber-400 shrink-0 mt-1" /> {b}
                  </li>
                ))}
              </ul>
              {onEditImage && (
                <div className="w-40 shrink-0">
                  {s.image_url ? (
                    <img src={s.image_url} alt="" className="w-full h-24 object-cover rounded-xl mb-1" />
                  ) : (
                    <div className="w-full h-24 bg-slate-50 rounded-xl mb-1 flex items-center justify-center text-slate-300 text-xs">без изображение</div>
                  )}
                  <button data-testid={`slide-image-btn-${i}`} onClick={() => onEditImage(i)}
                    className="text-xs font-bold text-sky-600 hover:underline">
                    {s.image_url ? "Смени изображение" : "Добави изображение"}
                  </button>
                </div>
              )}
              {!onEditImage && s.image_url && (
                <img src={s.image_url} alt="" className="w-40 h-24 object-cover rounded-xl shrink-0" />
              )}
            </div>
            {s.notes && <p className="mt-3 text-xs text-slate-400 italic border-t border-slate-100 pt-2">Бележки: {s.notes}</p>}
          </div>
        ))}
      </div>
    );
  }

  if (resource.type === "worksheet") {
    return (
      <div className="space-y-4" data-testid="content-worksheet">
        {c.instructions && <p className="text-slate-500 text-sm bg-sky-50 p-4 rounded-2xl">{c.instructions}</p>}
        {(c.questions || []).map((q, i) => (
          <div key={i} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
            <p className="font-semibold text-slate-900 mb-2">{i + 1}. {q.question}</p>
            {(q.options || []).length > 0 && (
              <ul className="space-y-1 pl-4">
                {q.options.map((o, j) => (
                  <li key={j} className="text-slate-600 text-sm">{String.fromCharCode(1040 + j)}) {o}</li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-emerald-600 font-semibold">Отговор: {q.answer}</p>
          </div>
        ))}
      </div>
    );
  }

  if (resource.type === "grading") {
    const tone = { 6: "emerald", 5: "sky", 4: "amber", 3: "orange", 2: "rose" }[c.grade] || "slate";
    const toneBg = { emerald: "bg-emerald-500", sky: "bg-sky-500", amber: "bg-amber-500", orange: "bg-orange-500", rose: "bg-rose-500", slate: "bg-slate-500" }[tone];
    return (
      <div className="space-y-4" data-testid="content-grading">
        <div className={`${toneBg} text-white rounded-3xl p-6 flex items-center justify-between`}>
          <div>
            <div className="text-sm opacity-90">{c.student_name || "Ученик"}</div>
            <div className="font-heading text-5xl font-black">{c.grade} <span className="text-2xl font-bold">{c.grade_label}</span></div>
          </div>
          <div className="text-right">
            <div className="font-heading text-3xl font-black">{c.earned_points}/{c.max_points}</div>
            <div className="opacity-90">{c.percentage}%</div>
          </div>
        </div>
        {c.summary && <p className="text-slate-600 text-sm bg-slate-50 rounded-2xl p-4">{c.summary}</p>}
        <div className="space-y-2">
          {(c.items || []).map((it, i) => (
            <div key={i} className={`bg-white border-l-4 rounded-2xl p-4 shadow-sm ${it.correct ? "border-emerald-400" : "border-rose-400"}`}>
              <div className="flex justify-between gap-3">
                <p className="font-semibold text-slate-900 text-sm">{i + 1}. {it.question}</p>
                <span className={`text-xs font-bold shrink-0 ${it.correct ? "text-emerald-600" : "text-rose-600"}`}>{it.points}/{it.max_points} т.</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">Отговор на ученика: <span className="font-medium text-slate-700">{it.student_answer}</span></p>
              {it.feedback && <p className="text-xs text-slate-400 mt-1">{it.feedback}</p>}
            </div>
          ))}
        </div>
        {c.recommendations && (
          <div className="bg-sky-50 rounded-2xl p-5">
            <h4 className="font-heading font-bold text-slate-900 mb-1">Препоръки</h4>
            <p className="text-sm text-slate-600">{c.recommendations}</p>
          </div>
        )}
      </div>
    );
  }

  // lesson-plan
  return (
    <div className="space-y-4" data-testid="content-lesson">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-slate-100 rounded-2xl p-5">
          <h4 className="font-heading font-bold text-slate-900 mb-2">Цели</h4>
          <ul className="space-y-1 text-sm text-slate-600 list-disc pl-4">{(c.objectives || []).map((o, i) => <li key={i}>{o}</li>)}</ul>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-5">
          <h4 className="font-heading font-bold text-slate-900 mb-2">Материали</h4>
          <ul className="space-y-1 text-sm text-slate-600 list-disc pl-4">{(c.materials || []).map((o, i) => <li key={i}>{o}</li>)}</ul>
          <p className="mt-2 text-xs text-slate-400">Времетраене: {c.duration}</p>
        </div>
      </div>
      {c.methodology && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5">
          <h4 className="font-heading font-bold text-slate-900 mb-1">Методология / подход</h4>
          <p className="text-sm text-slate-600">{c.methodology}</p>
        </div>
      )}
      <div className="bg-white border border-slate-100 rounded-2xl p-5">
        <h4 className="font-heading font-bold text-slate-900 mb-3">Ход на урока</h4>
        <div className="space-y-3">
          {(c.stages || []).map((s, i) => (
            <div key={i} className="border-l-2 border-sky-200 pl-4">
              <div className="flex justify-between"><span className="font-semibold text-slate-800 text-sm">{s.name}</span><span className="text-xs text-sky-500">{s.time}</span></div>
              {s.description && <p className="text-sm text-slate-600">{s.description}</p>}
              {(s.steps || []).length > 0 && (
                <ul className="text-sm text-slate-600 list-disc pl-4 mt-1">{s.steps.map((st, j) => <li key={j}>{st}</li>)}</ul>
              )}
              {s.teacher_actions && <p className="text-xs text-slate-500 mt-1"><span className="font-semibold">Учителят:</span> {s.teacher_actions}</p>}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-amber-50 rounded-2xl p-5"><h4 className="font-heading font-bold text-slate-900 mb-1">Домашна работа</h4><p className="text-sm text-slate-600">{c.homework}</p></div>
        <div className="bg-emerald-50 rounded-2xl p-5"><h4 className="font-heading font-bold text-slate-900 mb-1">Оценяване</h4><p className="text-sm text-slate-600">{c.assessment}</p></div>
      </div>
      {(c.interactive_ideas || []).length > 0 && (
        <div className="bg-sky-50 rounded-2xl p-5">
          <h4 className="font-heading font-bold text-slate-900 mb-1">Интерактивни идеи</h4>
          <ul className="text-sm text-slate-600 list-disc pl-4">{c.interactive_ideas.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </div>
      )}
      {c.tips && (
        <div className="bg-violet-50 rounded-2xl p-5"><h4 className="font-heading font-bold text-slate-900 mb-1">Полезни насоки</h4><p className="text-sm text-slate-600">{c.tips}</p></div>
      )}
    </div>
  );
}

export { colorMap };
